import React, { useEffect, useRef, useState } from "react";
import type { ThreadData } from "@liveblocks/core";
import {
  useCreateComment,
  useMarkThreadAsRead,
  useMarkThreadAsResolved,
  useMarkThreadAsUnresolved,
  useRoom,
  useSelf,
} from "@liveblocks/react";
import { deleteThreadAuthoritatively } from "pages/flyer/builder/utils/liveblocksAuth";
import Tooltip from "@material-ui/core/Tooltip";
import useTooltipStyles from "pages/flyer/builder/drawer/components/style";
import { useCommentsStore } from "store/comments";
import { CommentItem } from "./CommentItem";
import { CommentComposer } from "./CommentComposer";
import { notifyMentionsForBody } from "./notifyMentions";

type Props = {
  thread: ThreadData;
  onClose: () => void;
  /**
   * Tailwind-style classes to position the popover relative to the pin.
   * e.g. "left-6 top-0" or "right-6 bottom-0"
   */
  placementClassName?: string;
};

export const CommentThread: React.FC<Props> = ({ thread, onClose, placementClassName = "left-7 top-0" }) => {
  const createComment = useCreateComment();
  const room = useRoom();
  const markResolved = useMarkThreadAsResolved();
  const markUnresolved = useMarkThreadAsUnresolved();
  const markThreadAsRead = useMarkThreadAsRead();
  const markThreadReadLocal = useCommentsStore((s) => s.markThreadRead);
  const forgetThreadLocal = useCommentsStore((s) => s.forgetThread);
  const self = useSelf();

  // Mark the thread as read as soon as it opens — both in Liveblocks
  // (subscription cursor) and in our local localStorage-backed map (covers
  // threads the user isn't subscribed to). No need to reply or react.
  useEffect(() => {
    markThreadAsRead(thread.id);
    markThreadReadLocal(thread.id);
  }, [thread.id, markThreadAsRead, markThreadReadLocal]);
  const popoverRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tooltipClasses = useTooltipStyles();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        // Ignore clicks on the pin itself (pin will toggle via its own handler)
        const target = e.target as HTMLElement;
        if (target.closest("[data-comment-pin]")) return;
        // Ignore clicks on portaled comment UI (emoji picker, quick reactions, dropdown menu)
        if (target.closest("[data-comment-portal]")) return;
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isConfirmingDelete) {
          setIsConfirmingDelete(false);
          return;
        }
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, isConfirmingDelete]);

  const comments = thread.comments.filter((c) => c.body);
  const isResolved = !!thread.resolved;
  const firstAuthorId = thread.comments[0]?.userId;
  // Only the original author can delete the thread. Liveblocks' SDK-level
  // delete is rejected when other users have replied, so destructive ops
  // run through our `/liveblocks/delete-thread` proxy (admin secret) which
  // re-validates authorship server-side.
  const canDeleteThread = !!self?.id && self.id === firstAuthorId;

  // When the thread opens (or a new comment arrives), jump to the bottom so
  // the latest message is always visible. `auto` behavior avoids the jarring
  // smooth animation on initial open.
  const lastCommentId = comments[comments.length - 1]?.id;
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [thread.id, lastCommentId]);

  return (
    <div
      ref={popoverRef}
      data-comment-selectable
      className={`absolute z-30 w-[320px] select-text overflow-hidden rounded-xl border border-[#e2e2fc] bg-white shadow-[0_12px_32px_rgba(16,24,40,0.12)] ${placementClassName}`}
      style={{ userSelect: "text", WebkitUserSelect: "text" }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onContextMenu={(e) => {
        // Let the browser show its native menu (Copy / Select All / etc.)
        // but don't let tldraw or the canvas open their own context menu.
        e.stopPropagation();
      }}
    >
      <div className="flex items-center justify-between border-b border-[#f4f4f7] px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-[12px] font-medium text-[#130f26]/70">
          <span className="truncate">
            {comments.length} {comments.length === 1 ? "comment" : "comments"}
          </span>
          {isResolved && (
            <span className="shrink-0 rounded-full bg-[#d1fae5] px-2 py-[1px] text-[11px] text-[#047857]">
              Resolved
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Tooltip
            title={isResolved ? "Reopen" : "Resolve"}
            arrow
            placement="top"
            classes={{ tooltip: tooltipClasses.tooltip, arrow: tooltipClasses.arrow }}
          >
            <button
              type="button"
              onClick={() => (isResolved ? markUnresolved(thread.id) : markResolved(thread.id))}
              className="rounded p-1 text-[#130f26]/60 hover:bg-[#f4f4f7] hover:text-[#130f26]"
              aria-label={isResolved ? "Reopen" : "Resolve"}
            >
              {isResolved ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="8" cy="8" r="6" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M3.5 8.5l3 3 6-6.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </Tooltip>
          {canDeleteThread && (
            <Tooltip
              title="Delete"
              arrow
              placement="top"
              classes={{ tooltip: tooltipClasses.tooltip, arrow: tooltipClasses.arrow }}
            >
              <button
                type="button"
                onClick={() => setIsConfirmingDelete((v) => !v)}
                className={`rounded p-1 transition-colors ${
                  isConfirmingDelete ? "bg-[#fde7e7]" : "hover:bg-[#fde7e7]"
                }`}
                aria-label="Delete"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M2 4H3.33333H14"
                    stroke="#EE4360"
                    strokeWidth="1.33"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5.33301 4.00016V2.66683C5.33301 2.31321 5.47348 1.97407 5.72353 1.72402C5.97358 1.47397 6.31272 1.3335 6.66634 1.3335H9.33301C9.68663 1.3335 10.0258 1.47397 10.2758 1.72402C10.5259 1.97407 10.6663 2.31321 10.6663 2.66683V4.00016M12.6663 4.00016V13.3335C12.6663 13.6871 12.5259 14.0263 12.2758 14.2763C12.0258 14.5264 11.6866 14.6668 11.333 14.6668H4.66634C4.31272 14.6668 3.97358 14.5264 3.72353 14.2763C3.47348 14.0263 3.33301 13.6871 3.33301 13.3335V4.00016H12.6663Z"
                    stroke="#EE4360"
                    strokeWidth="1.33"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.66699 7.3335V11.3335"
                    stroke="#EE4360"
                    strokeWidth="1.33"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.33301 7.3335V11.3335"
                    stroke="#EE4360"
                    strokeWidth="1.33"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </Tooltip>
          )}
          {canDeleteThread && (
            <div
              className={`flex items-center gap-1 overflow-hidden whitespace-nowrap text-[11px] transition-all duration-200 ease-out ${
                isConfirmingDelete ? "ml-1 max-w-[180px] opacity-100" : "-ml-1 max-w-0 opacity-0"
              }`}
              aria-hidden={!isConfirmingDelete}
            >
              <span className="text-[#130f26]/70">Delete thread?</span>
              <button
                type="button"
                onClick={async () => {
                  setIsConfirmingDelete(false);
                  onClose();
                  forgetThreadLocal(thread.id);
                  try {
                    await deleteThreadAuthoritatively({
                      roomId: room.id,
                      threadId: thread.id,
                    });
                  } catch (err) {
                    console.error("[comments] delete thread failed", err);
                  }
                }}
                className="rounded px-1 font-semibold text-[#EE4360] hover:underline"
              >
                Yes
              </button>
              <span className="text-[#130f26]/40">·</span>
              <button
                type="button"
                onClick={() => setIsConfirmingDelete(false)}
                className="rounded px-1 font-semibold text-[#130f26]/70 hover:underline"
              >
                No
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#130f26]/60 hover:bg-[#f4f4f7] hover:text-[#130f26]"
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        // Auto-hiding scrollbar: thumb is transparent at rest and only fades
        // in on hover (desktop browsers otherwise show a permanent track on
        // the right edge, which felt heavy inside the small popover).
        className={`max-h-[320px] overflow-y-auto px-3 [scrollbar-gutter:stable] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar]:w-1.5 hover:[&::-webkit-scrollbar-thumb]:bg-[#130f26]/20 ${
          isResolved ? "opacity-60" : ""
        }`}
        style={{ scrollbarWidth: "thin", scrollbarColor: "transparent transparent" }}
        onMouseEnter={(e) => {
          // `scrollbar-color` isn't in lib.dom's `CSSStyleDeclaration` typings
          // yet; use `setProperty` so TS doesn't complain.
          (e.currentTarget as HTMLElement).style.setProperty("scrollbar-color", "rgba(19,15,38,0.2) transparent");
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.setProperty("scrollbar-color", "transparent transparent");
        }}
      >
        {comments.map((c) => (
          <CommentItem key={c.id} threadId={thread.id} comment={c} />
        ))}
      </div>

      <div className="border-t border-[#f4f4f7] bg-[#fafafd] p-3">
        <CommentComposer
          compact
          placeholder="Reply…"
          submitLabel="Reply"
          onSubmit={async (body, files) => {
            // Upload attached images first; failed uploads are dropped so a
            // network blip on one file doesn't block the reply.
            const attachments = files.length
              ? (
                  await Promise.all(
                    files.map(async (file) => {
                      try {
                        const local = room.prepareAttachment(file);
                        return await room.uploadAttachment(local);
                      } catch (err) {
                        console.error("[comments] reply attachment upload failed", err);
                        return null;
                      }
                    })
                  )
                ).filter((a): a is NonNullable<typeof a> => a !== null)
              : [];
            createComment({
              threadId: thread.id,
              body,
              // Only include attachments when present — see CommentsOverlay.
              ...(attachments.length ? { attachments } : {}),
            });
            // Replying to a resolved thread automatically reopens it — the
            // new activity means the discussion isn't actually settled, and
            // forcing the user to click Reopen first feels redundant.
            if (isResolved) {
              try {
                markUnresolved(thread.id);
              } catch (err) {
                console.error("[comments] auto-reopen on reply failed", err);
              }
            }
            if (self) {
              void notifyMentionsForBody(
                body,
                {
                  id: self.id,
                  name: (self.info as any)?.name || "An Oppizi user",
                  avatar: (self.info as any)?.avatar || "",
                  color: (self.info as any)?.color || "",
                },
                thread.id
              );
            }
          }}
        />
      </div>
    </div>
  );
};
